<template>
    <div class="full-height-layout fill">
        <mercur-card class="full-height-layout form mx-4 mb-3 px-4">
            <h2 class="font-weight-normal">
                <span v-if="isEdit">Edit stock level configuration</span>
                <span v-else>Add stock level configuration</span>
            </h2>
            <div class="row">
                <div class="col-6">
                    <pretty-select
                        v-model="$v.form.productConfigurationId.$model"
                        :options="productConfigurations"
                        :reduce="template => template.productConfigurationId"
                        label="productConfigurationName"
                        placeholder="Product configuration"
                        custom-placeholder="Select an option"
                        :always-show-placeholder="true"
                        :disabled="loading"
                        :class="{'form-invalid': $v.form.productConfigurationId.$error}">
                    </pretty-select>
                    <span class="form-note">
                        <span class="form-error" v-if="!$v.form.productConfigurationId.required">Required</span>
                    </span>
                </div>
                <div class="col-2">
                    <mercur-input
                        v-model="$v.form.threshold.$model"
                        type="number"
                        required
                        :disabled="loading"
                        :class="{'form-invalid': $v.form.threshold.$error}">
                        Threshold
                        <template slot="note">
                            <span class="form-error" v-if="!$v.form.threshold.required">Required</span>
                            <span class="form-error" v-if="!$v.form.threshold.integer">Must be an integer</span>
                            <span class="form-error" v-if="!$v.form.threshold.minValue">Must not be negative</span>

                        </template>
                    </mercur-input>
                </div>
                <div class="col-2">
                    <mercur-input
                        v-model="$v.form.sourceId.$model"
                        type="number"
                        required
                        readonly
                        :clearable="false"
                        :disabled="loading"
                        :class="{'form-invalid': $v.form.sourceId.$error}">
                        Source ID
                        <template slot="note">
                            <span class="form-error" v-if="!$v.form.sourceId.required">Required</span>
                            <span class="form-error" v-if="!$v.form.sourceId.integer">Must be an integer</span>
                        </template>
                    </mercur-input>
                </div>
                <div class="col-2" style="line-height: 72px;">
                    <mercur-checkbox
                        v-model="$v.form.active.$model"
                        required
                        :disabled="loading"
                        :class="{'form-invalid': $v.form.active.$error}">
                        Active
                    </mercur-checkbox>
                </div>
            </div>

            <div class="text-right">
                <mercur-button
                    :to="{ name: 'SupplierStockLevelConfigurations', params: { supplierId: this.supplierId } }"
                    class="btn btn-default"
                >
                    <i class="fas fa-chevron-left"></i>
                    Cancel
                </mercur-button>
                <mercur-button
                    @click="submit"
                    :disabled="$v.$invalid || loading"
                    class="btn btn-raised btn-yellow text-uppercase"
                >
                    Submit
                </mercur-button>
            </div>
        </mercur-card>
    </div>
</template>
<script>
import { required, integer, minValue } from 'vuelidate/lib/validators'
import { validationMixin } from 'vuelidate'
import { stripProperties } from '@/components/utils/Utils'
import CONFIG from '@root/config'
import PrettySelect from '@/components/utils/PrettySelect.vue'

export default {
    name: 'StockLevelConfigurationsAddEdit',
    components: { PrettySelect },
    mixins: [ validationMixin ],
    data () {
        return {
            loading: false,
            action: null,
            supplierDetails: {},
            productConfigurations: [],
            originalData: {},
            form: {
                productConfigurationId: null,
                threshold: null,
                sourceId: null,
                active: false,
            },
        }
    },
    validations: {
        form: {
            productConfigurationId: { required },
            threshold: { required, integer, minValue: minValue(0) },
            active: { required },
            sourceId: { required, integer },
        },
    },
    computed: {
        isEdit () {
            return !!this.$route.params.stockLevelConfigurationId
        },
    },

    methods: {
        loadProductConfigurations () {
            const url = CONFIG.API.ROUTES.SUPPLIERS.PRODUCT_CONFIGURATIONS.replace('{supplierId}', this.supplierId)
            this.addJob(url)
            this.$api.post(url)
                .then(({ data }) => this.$set(this, 'productConfigurations', data.data))
                .finally(() => this.finishJob(url))
        },
        cleanPayload () {
            const data = stripProperties(this.form)

            data.sourceId = Number(data.sourceId)
            data.threshold = Number(data.threshold)

            return data
        },
        submit () {
            const data = this.cleanPayload()

            if (this.isEdit) {
                this.action = CONFIG.API.ROUTES
                    .SUPPLIERS.STOCK_LEVEL_CONFIGURATIONS.UPDATE
                    .replace('{supplierId}', this.supplierId)
                    .replace('{stockLevelConfigurationId}', this.$route.params.stockLevelConfigurationId)
            }

            this.addJob(this.action)
            this.loading = true

            this.$api.post(this.action, data).then(({ data }) => {
                this.$root.$emit('notification:global', {
                    message: `Stock level configuration was created`,
                })

                setTimeout(() => {
                    this.$router.push({
                        name: 'SupplierStockLevelConfigurations',
                        params: {
                            supplierId: this.supplierId,
                        },
                    })
                }, 100)
            }).catch(data => {
                this.$root.$emit('notification:global', {
                    message: `Saving stock level configuration failed. Please try again.`,
                    type: 'error',
                    errors: data,
                })
            }).finally(() => {
                this.loading = false
                this.finishJob(this.action)
            })
        },
        fetchStockLevelConfiguration () {
            if (this.isEdit === false) {
                return
            }

            const url = CONFIG.API.ROUTES
                .SUPPLIERS.STOCK_LEVEL_CONFIGURATIONS.GET
                .replace('{supplierId}', this.supplierId)
                .replace('{stockLevelConfigurationId}', this.$route.params.stockLevelConfigurationId)
            this.loading = true
            this.addJob(url)
            this.$api.get(url).then(({ data }) => {
                this.originalData = data.data
                this.form = JSON.parse(JSON.stringify(this.originalData))
            }).finally(() => {
                this.loading = false
                this.finishJob(url)
            })
        },
    },
    created () {
        this.supplierData.then(supplierDetails => {
            this.form.sourceId = supplierDetails.sourceId
            this.form.supplierId = supplierDetails.supplierId
            this.supplierDetails = supplierDetails

            if (!this.isEdit) {
                this.action = CONFIG.API.ROUTES
                    .SUPPLIERS.STOCK_LEVEL_CONFIGURATIONS.ADD
                    .replace('{supplierId}', this.supplierId)
            }
        })
        this.loadProductConfigurations()
        this.fetchStockLevelConfiguration()
    },
}
</script>

<style lang="scss" scoped>
    .form {
        &__content {
            padding-left: 20px;
            padding-right: 20px;
        }
    }
</style>
